// TICKET-5 🔴 Fonction non testée : buildRestoredProfile (cascade de 3 fallbacks localStorage)
// Voir profile.test.ts pour les tests existants (createDefaultProfile, getProfileDisplayName)

import { UserProfile } from '@shared/types';

const PROFILE_KEY = 'indxone.profile.v1';
const MISSIO_OWNER_BACKUP_KEY = 'indxone.missio.owner.backup.v1';
const MISSIO_STORE_KEY = 'indxone.missio.v1';

type LegacyProfile = Partial<UserProfile>;
type LegacyMissioOwner = {
  prenom?: string;
  nom?: string;
  email?: string;
  societe?: string;
  role?: string;
  tjm?: number;
  jours?: number;
  status?: UserProfile['status'];
  netTarget?: number;
};

type LegacyMissioStore = {
  profil?: {
    prenom?: string;
    nom?: string;
    email?: string;
    societe?: string;
    role?: string;
  };
  settings?: {
    tjm?: number;
    jours?: number;
    status?: UserProfile['status'];
  };
};

function loadJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function createDefaultProfile(referenceTime = Date.now()): UserProfile {
  return {
    id: 'profile-default',
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    role: '',
    mode: 'expert',
    organizationType: 'freelance',
    address: '',
    postalCode: '',
    city: '',
    country: '',
    siret: '',
    vatNumber: '',
    iban: '',
    rib: '',
    bankName: '',
    logoDataUrl: '',
    status: 'sasu',
    netTarget: 2500,
    tjm: 650,
    daysPerMonth: 18,
    createdAt: referenceTime,
    updatedAt: referenceTime,
  };
}

export function getProfileDisplayName(profile: UserProfile): string {
  const name = `${profile.firstName} ${profile.lastName}`.trim();
  if (name) return name;
  if (profile.company) return profile.company;
  return 'Votre profil';
}

export function buildRestoredProfile(current?: Partial<UserProfile>): UserProfile {
  const now = Date.now();
  const defaults = createDefaultProfile(now);
  const stored = loadJson<LegacyProfile>(PROFILE_KEY) || {};
  const missioOwner = loadJson<LegacyMissioOwner>(MISSIO_OWNER_BACKUP_KEY) || {};
  const missioStore = loadJson<LegacyMissioStore>(MISSIO_STORE_KEY) || {};

  const firstName =
    normalizeString(missioStore.profil?.prenom) ||
    normalizeString(missioOwner.prenom) ||
    normalizeString(stored.firstName) ||
    normalizeString(current?.firstName);
  const lastName =
    normalizeString(missioStore.profil?.nom) ||
    normalizeString(missioOwner.nom) ||
    normalizeString(stored.lastName) ||
    normalizeString(current?.lastName);

  return {
    ...defaults,
    ...current,
    ...stored,
    id: normalizeString(stored.id) || normalizeString(current?.id) || defaults.id,
    firstName,
    lastName,
    email:
      normalizeString(missioStore.profil?.email) ||
      normalizeString(missioOwner.email) ||
      normalizeString(stored.email) ||
      normalizeString(current?.email),
    company:
      normalizeString(missioStore.profil?.societe) ||
      normalizeString(missioOwner.societe) ||
      normalizeString(stored.company) ||
      normalizeString(current?.company),
    role:
      normalizeString(missioStore.profil?.role) ||
      normalizeString(missioOwner.role) ||
      normalizeString(stored.role) ||
      normalizeString(current?.role),
    mode: stored.mode === 'guided' ? 'guided' : current?.mode === 'guided' ? 'guided' : 'expert',
    organizationType:
      stored.organizationType === 'tpe_pme' || stored.organizationType === 'public' || stored.organizationType === 'freelance'
        ? stored.organizationType
        : current?.organizationType === 'tpe_pme' || current?.organizationType === 'public' || current?.organizationType === 'freelance'
          ? current.organizationType
          : 'freelance',
    address: normalizeString(stored.address) || normalizeString(current?.address),
    postalCode: normalizeString(stored.postalCode) || normalizeString(current?.postalCode),
    city: normalizeString(stored.city) || normalizeString(current?.city),
    country: normalizeString(stored.country) || normalizeString(current?.country),
    siret: normalizeString(stored.siret) || normalizeString(current?.siret),
    vatNumber: normalizeString(stored.vatNumber) || normalizeString(current?.vatNumber),
    iban: normalizeString(stored.iban) || normalizeString(current?.iban),
    rib: normalizeString(stored.rib) || normalizeString(current?.rib),
    bankName: normalizeString(stored.bankName) || normalizeString(current?.bankName),
    logoDataUrl: normalizeString(stored.logoDataUrl) || normalizeString(current?.logoDataUrl),
    status:
      missioStore.settings?.status ||
      missioOwner.status ||
      stored.status ||
      current?.status ||
      defaults.status,
    netTarget:
      stored.netTarget !== undefined ? stored.netTarget :
      missioOwner.netTarget !== undefined ? missioOwner.netTarget :
      current?.netTarget !== undefined ? current.netTarget :
      defaults.netTarget,
    tjm:
      Number(missioStore.settings?.tjm || missioOwner.tjm || stored.tjm || current?.tjm || defaults.tjm) || defaults.tjm,
    daysPerMonth:
      Number(missioStore.settings?.jours || missioOwner.jours || stored.daysPerMonth || current?.daysPerMonth || defaults.daysPerMonth) ||
      defaults.daysPerMonth,
    updatedAt: now,
    createdAt: Number(stored.createdAt || current?.createdAt || defaults.createdAt) || defaults.createdAt,
  };
}
